import { Link } from 'react-router-dom';
import { Bot, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

export type ResourceKind = 'agent' | 'skill';

export function ResourceIcon({ kind }: { kind: ResourceKind }) {
  if (kind === 'agent')
    return <Bot className="h-4 w-4 shrink-0 text-muted-foreground" />;
  return <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

export interface ResourceCardProps {
  icon: ReactNode;
  title: string;
  description?: string;
  /** Plugin-installed resources carry a `resourceKey` (e.g. `agents/foo.md`)
   *  — show it in a small mono footer so the user can map back to the repo. */
  resourceKey?: string;
  href: string;
}

/**
 * Compact card used on the PluginDetail Resources tab for the agents/skills
 * installed by a plugin. Mirrors the catalog `PluginGridCard` `size="compact"`
 * proportions (p-4, text-sm, h-4 icon, 2-line description) so the page reads
 * consistently top-to-bottom.
 */
export function ResourceCard({
  icon,
  title,
  description,
  resourceKey,
  href,
}: ResourceCardProps) {
  return (
    <Link
      to={href}
      className="rounded-xl border border-border bg-card p-4 flex flex-col gap-2 transition-colors hover:border-primary-500 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="text-sm font-semibold text-foreground truncate flex-1">
          {title}
        </h4>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground line-clamp-2">
          {description}
        </p>
      )}
      {resourceKey && (
        <p className="font-mono text-[10px] text-muted-foreground/80 truncate mt-auto pt-1">
          {resourceKey}
        </p>
      )}
    </Link>
  );
}
